import { apiClient, clean } from "./apiClient";
import { diaryService } from "./diaryService";
import type { PageRequest, PageResponse } from "@/types/api";
import type { DiaryEntryDetail, DiaryEntryFilter, DiaryEntrySummary } from "@/types/entities";
import type { AvailabilityCreate, SlotSettings } from "@/types/availability";

/**
 * Property availability management. Availability is an AVAILABILITY diary entry
 * (atlas stamps it sync_alto=CHECKPOINT, so it is not pushed to the CRM); the
 * booking service projects bookable blocks from it.
 */
export const availabilityService = {
  create: (body: AvailabilityCreate): Promise<DiaryEntryDetail> =>
    apiClient.post("/diary-entries", body).then((r) => r.data),

  /**
   * AVAILABILITY-only diary entries for a property (uses the entryType filter).
   *
   * The property already scopes the query, so the header's branch is kept out: availability
   * entries are saved without a branch, and branch-scoped (client) users always have one
   * forced in the header — injecting it filtered every availability window out for them.
   */
  listForProperty: (
    propertyId: string,
    page: PageRequest = { page: 0, size: 100 },
  ): Promise<PageResponse<DiaryEntrySummary>> =>
    diaryService.list(
      { propertyId, entryType: "AVAILABILITY", skipGlobalBranchId: true } as DiaryEntryFilter,
      page,
    ),

  remove: (id: string): Promise<void> => apiClient.delete(`/diary-entries/${id}`).then(() => undefined),

  /**
   * Resolved (cascaded) slot settings for a scope. The scope is exactly what the
   * caller passes — the header's legal entity / branch are deliberately kept out,
   * so a scope the caller left blank stays blank in the query.
   */
  getSettings: (params: {
    propertyId?: string;
    branchId?: string;
    legalEntityId?: string;
  }): Promise<SlotSettings> =>
    apiClient
      .get("/booking/slot-settings", { params: { ...clean(params), skipGlobalContext: true } })
      .then((r) => {
        // Drop the legacy single-user field — the app now uses the `users`
        // priority list, and carrying it would leak back into save payloads.
        const { defaultUserId: _legacy, ...rest } = (r.data ?? {}) as SlotSettings & {
          defaultUserId?: unknown;
        };
        return rest as SlotSettings;
      }),

  /**
   * The slot settings that actually APPLY to a property, plus `resolvedScope` /
   * `resolvedFromId` saying which tier they came from.
   *
   * Anchored server-side on the property's own branch association and legal entity, so it finds
   * inherited settings that `getSettings` (which only reports one exact scope) does not. Read-only
   * — inherited values must never be written back down onto the property.
   */
  getEffectiveSettings: (propertyId: string): Promise<SlotSettings> =>
    apiClient.get("/booking/slot-settings/effective", { params: { propertyId } }).then((r) => {
      const { defaultUserId: _legacy, ...rest } = (r.data ?? {}) as SlotSettings & { defaultUserId?: unknown };
      return rest as SlotSettings;
    }),

  /** Upsert slot settings for the scope carried on the body. */
  saveSettings: (
    body: SlotSettings & {
      propertyRefId?: string | null;
      branchId?: string | null;
      legalEntityId?: string | null;
      id?: string | null;
      marketType?: string | null;
    },
  ): Promise<SlotSettings> =>
    apiClient.put("/booking/slot-settings", body).then((r) => {
      const { defaultUserId: _legacy, ...rest } = (r.data ?? {}) as SlotSettings & { defaultUserId?: unknown };
      return rest as SlotSettings;
    }),
};